import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  connectionRows,
  CreateApiKeyDocument,
  type CreateApiKeyInput,
  GetApiKeysDocument,
  RevokeApiKeyDocument,
  UpdateApiKeyDocument,
  type UpdateApiKeyInput,
} from "#app/lib/data";
import { addApiKeyToCache, removeApiKeyFromCache } from "./api-key-cache-utils";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { useAnalytics, AnalyticsContext, SettingsEvent } from "#app/lib/analytics";

const ALL_API_KEYS_VARS = { limit: 100 };

function stripTypename<T extends Record<string, any>>(obj: T): Omit<T, "__typename"> {
  const { __typename: _, ...rest } = obj;
  return rest;
}

function buildApiKeysVars(userId?: string) {
  if (!userId) return ALL_API_KEYS_VARS;
  return { limit: 100, where: { userId: { eq: userId } } };
}

export function useApiKeys(userId?: string) {
  const variables = useMemo(() => buildApiKeysVars(userId), [userId]);

  const { data, loading, error } = useQuery(GetApiKeysDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const connection = data?.apiKeys;
  const apiKeys = useMemo(
    () => connectionRows(connection),
    [connection],
  );

  return {
    apiKeys,
    total: connection?.total ?? 0,
    isLoading: loading,
    error,
  };
}

export function useApiKeyMutations() {
  const { track } = useAnalytics();
  const [createMutation] = useMutation(CreateApiKeyDocument);
  const [updateMutation] = useMutation(UpdateApiKeyDocument);
  const [revokeMutation] = useMutation(RevokeApiKeyDocument);

  const createApiKey = useCallback(
    async (input: CreateApiKeyInput) => {
      try {
        const cleanInput = input.permissions
          ? { ...input, permissions: stripTypename(input.permissions) }
          : input;

        const { data } = await createMutation({
          variables: { input: cleanInput },
          update(cache, { data }) {
            const result = data?.createApiKey;
            if (result?.__typename !== "ApiKey") return;
            addApiKeyToCache(cache, result);
          },
        });

        const result = data?.createApiKey;
        if (result?.__typename === "ApiKey") {
          track({ context: AnalyticsContext.Settings, event: SettingsEvent.Create, label: "api_key" });
          toastManager.add({ title: "API key created", type: "success" });
          return result;
        }
        // ValidationError / NotFoundError / PermissionDenied — show the
        // server-provided message so the user sees the actual cause (e.g.
        // duplicate name) instead of the generic fallback.
        toastManager.add({ title: resultMessage(result) ?? "Failed to create API key", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create API key");
        return null;
      }
    },
    [createMutation, track],
  );

  const updateApiKey = useCallback(
    async (apiKeyId: string, input: UpdateApiKeyInput) => {
      try {
        const cleanInput = input.permissions
          ? { ...input, permissions: stripTypename(input.permissions) }
          : input;

        const { data } = await updateMutation({
          variables: { apiKeyId, input: cleanInput },
        });

        const result = data?.updateApiKey;
        if (result?.__typename === "ApiKey") {
          track({ context: AnalyticsContext.Settings, event: SettingsEvent.Update, label: "api_key" });
          toastManager.add({ title: "API key updated", type: "success" });
          return result;
        }
        toastManager.add({ title: resultMessage(result) ?? "Failed to update API key", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update API key");
        return null;
      }
    },
    [updateMutation, track],
  );

  const revokeApiKey = useCallback(
    async (apiKeyId: string) => {
      try {
        await revokeMutation({
          variables: { apiKeyId },
          update(cache, { data }) {
            const result = data?.revokeApiKey;
            if (result?.__typename !== "ApiKey") return;

            removeApiKeyFromCache(cache, result.apiKeyId);
            const cacheId = cache.identify(result);
            // `identify` answers `undefined` for an object it cannot key, and
            // the cache reads an absent `id` as the ROOT — the eviction would
            // drop a ROOT_QUERY field instead of this entity.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        track({ context: AnalyticsContext.Settings, event: SettingsEvent.Revoke, label: "api_key" });
        toastManager.add({ title: "API key revoked", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to revoke API key");
      }
    },
    [revokeMutation, track],
  );

  return { createApiKey, updateApiKey, revokeApiKey };
}
